import {
  getWorkspaceAuthSession,
  setWorkspaceAuthSession,
  type WorkspaceAuthSession,
} from '@amglobal/auth-shared'
import type { WorkspaceDesk } from '@/config/workspace-desk'
import { deskPortalOrigin, isDeskHost } from '@/config/workspace-desk-host'
import { defaultLandingPathForDesk } from '@/config/workspace-desk-routes'
import { resolveStaffHomeDesk } from '@/shared/lib/staff-capabilities'

const HANDOFF_HASH_PREFIX = '#amgDeskHandoff='
const DESK_NAV_QUERY = 'deskNav'

type HandoffPayload = {
  v: 1
  accessToken: string
  refreshToken: string
  sessionId: string
  deviceId: string
  user: WorkspaceAuthSession['user']
}

function encodeHandoff(session: WorkspaceAuthSession): string {
  const payload: HandoffPayload = {
    v: 1,
    accessToken: session.accessToken,
    refreshToken: session.refreshToken,
    sessionId: session.sessionId,
    deviceId: session.deviceId,
    user: session.user,
  }
  const json = JSON.stringify(payload)
  // Prefer base64url so the hash stays URL-safe.
  const bytes = new TextEncoder().encode(json)
  let binary = ''
  for (const byte of bytes) binary += String.fromCharCode(byte)
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '')
}

function decodeHandoff(raw: string): HandoffPayload | null {
  try {
    const padded = raw.replace(/-/g, '+').replace(/_/g, '/')
    const padLen = (4 - (padded.length % 4)) % 4
    const b64 = padded + '='.repeat(padLen)
    const binary = atob(b64)
    const bytes = Uint8Array.from(binary, (ch) => ch.charCodeAt(0))
    const json = new TextDecoder().decode(bytes)
    const parsed = JSON.parse(json) as HandoffPayload
    if (parsed?.v !== 1 || !parsed.accessToken || !parsed.refreshToken || !parsed.user) {
      return null
    }
    return parsed
  } catch {
    return null
  }
}

/**
 * Read a cross-desk session handoff from the URL hash (never sent to the server) and persist it.
 * Must run before auth/session queries hydrate.
 */
export function consumeDeskSessionHandoff(
  location: Pick<Location, 'hash' | 'pathname' | 'search'> = window.location,
): boolean {
  const hash = location.hash ?? ''
  if (!hash.startsWith(HANDOFF_HASH_PREFIX)) return false

  const encoded = hash.slice(HANDOFF_HASH_PREFIX.length)
  const payload = decodeHandoff(decodeURIComponent(encoded))
  if (!payload) return false

  setWorkspaceAuthSession({
    accessToken: payload.accessToken,
    refreshToken: payload.refreshToken,
    sessionId: payload.sessionId,
    deviceId: payload.deviceId,
    user: payload.user,
  })

  const cleanUrl = `${location.pathname}${location.search}`
  window.history.replaceState(null, '', cleanUrl)
  return true
}

function withDeskNavMarker(path: string): string {
  const url = new URL(path, 'https://placeholder.local')
  url.searchParams.set(DESK_NAV_QUERY, '1')
  return `${url.pathname}${url.search}`
}

/** True when this navigation already came from a desk handoff — do not bounce again. */
export function isDeskNavMarked(
  search: string = typeof window !== 'undefined' ? window.location.search : '',
): boolean {
  return new URLSearchParams(search).get(DESK_NAV_QUERY) === '1'
}

/** Absolute URL for a staff member's home desk landing page (with optional session handoff). */
export function buildStaffHomePortalUrl(
  capabilities: readonly string[],
  roles: readonly string[],
  currentHost: string = typeof window !== 'undefined' ? window.location.host : '',
  options?: { includeHandoff?: boolean },
): { homeDesk: WorkspaceDesk; url: string } {
  const homeDesk = resolveStaffHomeDesk(capabilities, roles)
  const path = withDeskNavMarker(defaultLandingPathForDesk(homeDesk, roles, capabilities))
  const origin = deskPortalOrigin(homeDesk, currentHost)
  let url = `${origin}${path}`

  if (options?.includeHandoff !== false) {
    const session = getWorkspaceAuthSession()
    if (session) {
      url = `${url}${HANDOFF_HASH_PREFIX}${encodeURIComponent(encodeHandoff(session))}`
    }
  }

  return { homeDesk, url }
}

/**
 * When signed in on the wrong desk subdomain, hard-navigate to the home portal
 * and carry the login session in the URL hash so the target origin can adopt it.
 * Returns true when a redirect was started (caller should stop rendering).
 */
export function redirectToStaffHomePortalIfWrongDesk(
  currentDesk: WorkspaceDesk | null,
  capabilities: readonly string[],
  roles: readonly string[],
  hostname: string = typeof window !== 'undefined' ? window.location.hostname : '',
  host: string = typeof window !== 'undefined' ? window.location.host : '',
  search: string = typeof window !== 'undefined' ? window.location.search : '',
): boolean {
  if (!currentDesk || !isDeskHost(hostname)) {
    return false
  }

  // Already arrived via a desk handoff — never bounce again (stops pro↔hr loops).
  if (isDeskNavMarked(search)) {
    return false
  }

  const { homeDesk, url } = buildStaffHomePortalUrl(capabilities, roles, host, {
    includeHandoff: true,
  })
  if (homeDesk === currentDesk) {
    return false
  }

  if (typeof window !== 'undefined') {
    try {
      window.location.replace(url)
    } catch {
      window.location.href = url
    }
  }
  return true
}
